import { create } from "zustand";
import { api, uploadFile } from "../utils/api";
import { ApiError } from "../utils/api";
import type { RunSummary, RunDetail, TrackWeek } from "../types";

// #448: `association` says whether the processed run landed in a week. An
// "unassigned" run is not in any week workspace, so it is not a plain success.
export interface JobStatus {
  status: string;
  runId?: string;
  error?: string;
  trackWeekId?: string | null;
  association?: "matched" | "unassigned" | null;
}

// #488: the upload response. `duplicate` means this team already has the file;
// the job returned may be a teammate's, named by `uploadedBy`.
export interface UploadResult {
  jobId: string;
  status: string;
  runId?: string | null;
  duplicate?: boolean;
  uploadedBy?: string | null;
  uploadedByYou?: boolean;
}

interface RunState {
  runs: RunSummary[];
  // #546: which "team|week" `runs` belongs to (the most recent fetch).
  runsKey: string | null;
  // #546: runs per "team|week". Pages share this store, and a response for one
  // week can land after a request for another (a week switch, an upload refresh);
  // a week page reads ITS entry, never whichever fetch finished last.
  runsByKey: Record<string, RunSummary[]>;
  // #546: loading/error of each week's own request (a failure or a pending
  // request for another week must not show on this one: Codex round 2)
  runsStatusByKey: Record<string, { loading: boolean; error: string | null }>;
  trackWeeks: TrackWeek[];
  // #741: the week list's own request, per team. A failed or pending request must not
  // read as "this series has no weeks".
  trackWeeksStatus: { teamId: string | null; state: "idle" | "loading" | "loaded" | "error" };
  _cachedTeamId: string | null;
  selectedTrackWeekId: string | null;
  // Per-week selected car for the Fuel/Pit switcher (#279) — shared so a car
  // chosen on Fuel carries to Pit within the same week, reset per week.
  selectedCarByWeek: Record<string, string>;
  // #547: the run page's week, so the header can show where the run lives
  // (the /runs/:id URL carries no team or week).
  runContext: {
    runId: string;
    teamId: string;
    // the run's own week, loaded by the run page itself (never the shared week list,
    // which another page may be filling for another team: Codex on #547)
    week: { id: string; teamId: string; trackName: string; seriesName: string | null;
            weekNumber: number | null; displayWeekNumber?: number | null } | null;
  } | null;
  loading: boolean;
  // Distinguishes "the request failed" from "there are genuinely no runs" so
  // boards don't render a confident empty state over a network error.
  error: string | null;
  uploading: boolean;
  uploadProgress: number;

  fetchTrackWeeks: (teamId: string, seriesName?: string) => Promise<void>;
  setTrackWeek: (id: string) => void;
  setSelectedCar: (trackWeekId: string, carId: string) => void;
  setRunContext: (ctx: RunState["runContext"]) => void;
  fetchRuns: (teamId: string, trackWeekId?: string) => Promise<void>;
  fetchRunDetail: (runId: string) => Promise<RunDetail>;
  uploadIbt: (file: File, teamId: string) => Promise<UploadResult>;
  deleteRun: (runId: string) => Promise<void>;
  pollJobStatus: (jobId: string) => Promise<JobStatus>;
}

// latest request number per "team|week": only a newer request for the SAME week
// supersedes a response (#546, Codex: a global counter let an upload refresh of
// week A discard week B's answer)
const runsRequest: Record<string, number> = {};
let latestAnyRequest = 0;
// #558: only the latest week-list request may land; a late answer for another
// team (or series filter) would otherwise replace the weeks on screen
let trackWeeksRequest = 0;   // the legacy `runs`/`loading`/`error` follow the latest request overall
const NO_RUNS: RunSummary[] = [];
const IDLE = { loading: false, error: null as string | null };
const PENDING = { loading: true, error: null as string | null };   // requested soon, never seen

/** The key `runs` is stored under (#546). */
export function runsKeyFor(teamId: string, trackWeekId?: string | null): string {
  return `${teamId}|${trackWeekId ?? ""}`;
}

/** The API returns at most 100 runs a page (default 50). A week is compared whole,
 *  so a week-scoped fetch pages until a short page (#813), stopping at 20 pages as a
 *  safety net. A team-wide fetch is a picker: one page of 100. A failed page fails
 *  the fetch, never a silently partial list. */
export const RUN_PAGE_SIZE = 100;
const MAX_RUN_PAGES = 20;

export async function fetchRunPages(teamId: string, trackWeekId?: string): Promise<RunSummary[]> {
  const base = `/api/runs?team_id=${teamId}${trackWeekId ? `&track_week_id=${trackWeekId}` : ""}&limit=${RUN_PAGE_SIZE}`;
  const all: RunSummary[] = [];
  const seen = new Set<string>();
  for (let page = 0; page < (trackWeekId ? MAX_RUN_PAGES : 1); page++) {
    const rows = await api<RunSummary[]>(`${base}&offset=${page * RUN_PAGE_SIZE}`);
    // An upload between two pages shifts the offsets and repeats a boundary run
    // (Codex, #820): keep the first copy. Offsets and the stop still use raw pages.
    for (const r of rows) if (!seen.has(r.id)) { seen.add(r.id); all.push(r); }
    if (rows.length < RUN_PAGE_SIZE) return all;
  }
  if (trackWeekId) console.warn(`Stopped after ${MAX_RUN_PAGES * RUN_PAGE_SIZE} runs for week ${trackWeekId}`);
  return all;
}

export const useRunStore = create<RunState>((set, get) => ({
  runs: [],
  runsKey: null,
  runsByKey: {},
  runsStatusByKey: {},
  trackWeeks: [],
  trackWeeksStatus: { teamId: null, state: "idle" },
  _cachedTeamId: null,
  runContext: null,
  selectedTrackWeekId: null,
  selectedCarByWeek: {},
  loading: false,
  error: null,
  uploading: false,
  uploadProgress: 0,

  fetchTrackWeeks: async (teamId: string, seriesName?: string) => {
    const req = ++trackWeeksRequest;
    try {
      // Clear cache when switching teams
      if (get()._cachedTeamId !== teamId) {
        set({ trackWeeks: [], selectedTrackWeekId: null, _cachedTeamId: teamId });
      }
      set({ trackWeeksStatus: { teamId, state: "loading" } });
      let url = `/api/track-weeks?team_id=${teamId}`;
      if (seriesName) url += `&series_name=${encodeURIComponent(seriesName)}`;
      const weeks = await api<TrackWeek[]>(url);
      if (req !== trackWeeksRequest || get()._cachedTeamId !== teamId) return;
      const currentSelectedId = get().selectedTrackWeekId;
      const preserved = currentSelectedId
        ? weeks.find((w) => w.id === currentSelectedId)
        : null;
      const active = preserved || weeks.find((w) => w.isActive) || weeks[0];
      set({
        trackWeeks: weeks,
        selectedTrackWeekId: active?.id || null,
        _cachedTeamId: teamId,
        trackWeeksStatus: { teamId, state: "loaded" },
      });
    } catch {
      if (req === trackWeeksRequest) set({ trackWeeksStatus: { teamId, state: "error" } });
    }
  },

  setTrackWeek: (id: string) => {
    set({ selectedTrackWeekId: id });
  },

  setSelectedCar: (trackWeekId: string, carId: string) => {
    set((s) => ({ selectedCarByWeek: { ...s.selectedCarByWeek, [trackWeekId]: carId } }));
  },

  setRunContext: (ctx) => set({ runContext: ctx }),

  fetchRuns: async (teamId: string, trackWeekId?: string) => {
    const key = runsKeyFor(teamId, trackWeekId);
    const req = (runsRequest[key] = (runsRequest[key] ?? 0) + 1);
    const any = ++latestAnyRequest;
    const failed = "Couldn't load runs. Check your connection and try again.";
    set((st) => ({
      loading: true, error: null,
      runsStatusByKey: { ...st.runsStatusByKey, [key]: { loading: true, error: null } },
    }));
    try {
      const runs = await fetchRunPages(teamId, trackWeekId);
      set((st) => ({
        // #546: this week's slot takes only its latest request's answer
        ...(req === runsRequest[key] ? {
          runsByKey: { ...st.runsByKey, [key]: runs },
          runsStatusByKey: { ...st.runsStatusByKey, [key]: { loading: false, error: null } },
        } : {}),
        // the legacy slot (Setup Compare, Handling) follows the latest request overall
        ...(any === latestAnyRequest ? { runs, runsKey: key, loading: false } : {}),
      }));
    } catch {
      set((st) => ({
        ...(req === runsRequest[key] ? {
          runsStatusByKey: { ...st.runsStatusByKey, [key]: { loading: false, error: failed } },
        } : {}),
        ...(any === latestAnyRequest ? { loading: false, error: failed } : {}),
      }));
    }
  },

  fetchRunDetail: async (runId: string) => {
    return api<RunDetail>(`/api/runs/${runId}`);
  },

  uploadIbt: async (file: File, teamId: string) => {
    set({ uploading: true, uploadProgress: 0 });
    try {
      const result = (await uploadFile("/api/upload", file, { team_id: teamId }, (pct) => {
        set({ uploadProgress: pct });
      })) as UploadResult;
      set({ uploading: false, uploadProgress: 100 });
      return result;
    } catch (err) {
      set({ uploading: false, uploadProgress: 0 });
      throw err;
    }
  },

  deleteRun: async (runId: string) => {
    await api(`/api/runs/${runId}`, { method: "DELETE" });
    set((s) => ({
      runs: s.runs.filter((r) => r.id !== runId),
      runsByKey: Object.fromEntries(
        Object.entries(s.runsByKey).map(([k, list]) => [k, list.filter((r) => r.id !== runId)]),
      ),
    }));
  },

  pollJobStatus: async (jobId: string) => {
    const poll = async (
      missingRetries = 0
    ): Promise<JobStatus> => {
      let result: JobStatus;
      try {
        result = await api<JobStatus>(`/api/jobs/${jobId}`);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 404 &&
          missingRetries < 4
        ) {
          await new Promise((r) => setTimeout(r, 400));
          return poll(missingRetries + 1);
        }
        throw err;
      }

      if (result.status === "complete") {
        return result;
      }
      if (result.status === "failed") {
        throw new Error(result.error || "Telemetry processing failed");
      }
      await new Promise((r) => setTimeout(r, 2000));
      return poll(0);
    };
    return poll();
  },
}));

/** The runs of one team's week (#546): empty until that week's fetch lands. */
export function useRunsFor(teamId: string | null | undefined, trackWeekId: string | null | undefined): RunSummary[] {
  return useRunStore((st) => (teamId ? st.runsByKey[runsKeyFor(teamId, trackWeekId)] ?? NO_RUNS : NO_RUNS));
}

/** The loading/error state of one team's week (#546). */
export function useRunsStatusFor(
  teamId: string | null | undefined, trackWeekId: string | null | undefined,
): { loading: boolean; error: string | null } {
  return useRunStore((st) =>
    teamId && trackWeekId ? st.runsStatusByKey[runsKeyFor(teamId, trackWeekId)] ?? PENDING : IDLE);
}
